import type { DraftDocumentVersionHistoryItem } from '@/hooks/useDraftDocuments';
import { formatDate, formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';

export const formatDateTime = (
  value?: string | null,
  locale: RegionalDateLocale = 'uk',
) => {
  return formatRegionalDate(value, locale, { includeTime: true });
};

export function formatRecordedTimestampLabel(
  value?: string | null,
  actorName?: string | null,
  locale: RegionalDateLocale = 'uk',
): string {
  const timestamp = formatDateTime(value, locale);
  const author = actorName?.trim();
  return author ? `Recorded ${timestamp} by ${author}` : `Recorded ${timestamp}`;
}

export function groupDraftVersionsByDay(
  versions: DraftDocumentVersionHistoryItem[],
  locale: RegionalDateLocale = 'uk',
) {
  const groups: { label: string; versions: DraftDocumentVersionHistoryItem[] }[] = [];
  for (const version of versions) {
    const label = formatHistoryDay(version.created_at, locale);
    const last = groups[groups.length - 1];
    if (last?.label === label) {
      last.versions.push(version);
    } else {
      groups.push({ label, versions: [version] });
    }
  }
  return groups;
}

export function formatHistoryDay(
  value?: string | null,
  locale: RegionalDateLocale = 'uk',
): string {
  if (!value) return 'Unknown date';
  return formatRegionalDate(value, locale, { month: 'long' });
}

export function formatHistoryTime(value?: string | null): string {
  if (!value) return '';
  const formatted = formatDate(value, 'HH:mm');
  return formatted === '-' ? '' : formatted;
}

export function draftVersionActivityLabel(
  version: DraftDocumentVersionHistoryItem,
  locale: RegionalDateLocale = 'uk',
): string {
  const summary = version.change_summary?.trim();
  const timestamp = formatDateTime(version.created_at, locale);
  const author = version.actor_user_name?.trim();
  const byAuthor = author ? ` by ${author} ·` : '';
  if (summary?.startsWith('Restored from')) return `${summary} · Saved${byAuthor} ${timestamp}`;
  if (version.version_number === 1) return `Created${byAuthor} ${timestamp}`;
  return `Saved${byAuthor} ${timestamp}`;
}
